import { PACK_SIZES, PackSize, PACK_VARIANT_IDS } from "./PackSizes";

export interface PackAllocationInput {
  recipeId: string;
  gramsNeeded: number; // total grams needed for the plan
}

export interface PackAllocationResult {
  recipeId: string;
  totalGrams: number;
  packs: {
    size: PackSize;
    quantity: number;
    variantId: string;
  }[];
  overfill: number;
}

const OVERFILL_TOLERANCE_PERCENT = 0.1;
const OVERFILL_TOLERANCE_MIN = 100;

export function allocatePacksForRecipe(
  input: PackAllocationInput
): PackAllocationResult {
  const { gramsNeeded } = input;
  const packs: Record<PackSize, number> = {
    250: 0,
    400: 0,
    600: 0,
    800: 0,
  };

  let remaining = gramsNeeded;

  // 1. Greedy fill largest → smallest
  for (const size of [...PACK_SIZES].sort((a, b) => b - a)) {
    const qty = Math.floor(remaining / size);
    packs[size] = qty;
    remaining -= qty * size;
  }

  // 2. If remaining > 0, add one smallest pack
  if (remaining > 0) {
    packs[250] += 1;
    remaining -= 250;
  }

  // 3. Compute totals
  const totalGrams =
    250 * packs[250] + 400 * packs[400] + 600 * packs[600] + 800 * packs[800];

  const overfill = totalGrams - gramsNeeded;

  const allowedOverfill = Math.max(
    gramsNeeded * OVERFILL_TOLERANCE_PERCENT,
    OVERFILL_TOLERANCE_MIN
  );

  // 4. Try to reduce overfill by removing one 250g or swapping
  if (overfill > allowedOverfill && packs[250] > 0) {
    packs[250] -= 1;
  }

  const resultTotal =
    250 * packs[250] + 400 * packs[400] + 600 * packs[600] + 800 * packs[800];

  return {
    recipeId: input.recipeId,
    totalGrams: resultTotal,
    packs: PACK_SIZES.filter((s) => packs[s] > 0).map((s) => ({
      size: s,
      quantity: packs[s],
      variantId: PACK_VARIANT_IDS[s],
    })),
    overfill: resultTotal - gramsNeeded,
  };
}
